import { type SessionStoredValue } from "@sg-protect/core";
import { MixpanelPropertiesProvider } from "@workleap/telemetry/react";
import { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MixpanelProperty } from "./tracking/mixpanelProperty.ts";
import { useUserIdentification } from "./useUserIdentification.ts";

export interface Tenant {
    tenantId: string;
}

export interface TenantContextType {
    currentTenant: Tenant | null;
    tenants: Tenant[];
    setTenant: (tenant: Tenant | null) => void;
}

const TenantContext = createContext<TenantContextType | null>(null);

export const useTenantContext = () => {
    const context = useContext(TenantContext);

    if (!context) {
        throw new Error("useTenantContext must be used within a TenantProvider");
    }

    return context;
};

interface TenantProviderProps extends PropsWithChildren {
    tenants: Tenant[];
    storage: SessionStoredValue;
}

export function TenantProvider({ children, tenants, storage }: TenantProviderProps) {
    const { identifyTenant } = useUserIdentification();

    const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(() => {
        const storedId = storage.get();
        const stored = storedId ? (tenants.find(t => t.tenantId === storedId) ?? null) : null;
        const resolved = stored ?? tenants[0] ?? null;

        if ((resolved?.tenantId ?? null) !== storedId) {
            storage.set(resolved?.tenantId ?? null);
        }

        return resolved;
    });

    const setTenant = useCallback(
        (tenant: Tenant | null) => {
            setCurrentTenantState(tenant);
            storage.set(tenant?.tenantId ?? null);
        },
        [storage]
    );

    useEffect(() => {
        identifyTenant(currentTenant?.tenantId ?? null);
    }, [currentTenant, identifyTenant]);

    const value = useMemo<TenantContextType>(
        () => ({ currentTenant, tenants, setTenant }),
        [currentTenant, tenants, setTenant]
    );

    const tenantTelemetryContext = useMemo(() => {
        return currentTenant ? { [MixpanelProperty.TenantId]: currentTenant.tenantId } : {};
    }, [currentTenant]);

    return (
        <TenantContext.Provider value={value}>
            <MixpanelPropertiesProvider value={tenantTelemetryContext}>{children}</MixpanelPropertiesProvider>
        </TenantContext.Provider>
    );
}
